import React, { useContext, useState, useEffect } from 'react';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import { Typography } from '@wds/typography';
import { StaticContext } from '@sm/webassets';

import { Query } from '@apollo/react-components';
import developerApplications from './home.graphql';

import DeveloperWebBasePage from '../DeveloperWebBasePage';

import waitForUsabilla from '~app/helpers/waitForUsabilla';

import './home.scss';

const Home = () => {
  const {
    environment: { domain, subdomain },
    user,
    url,
  } = useContext(StaticContext);
  const [targetUrl, setTargetUrl] = useState(url);

  const developerEUSubdomain = 'developer.eu';
  const developerSubdomain = 'developer';

  const prependUrl = subdomain !== developerSubdomain && subdomain !== developerEUSubdomain ? '/developer' : '';
  const prependStatic =
    subdomain !== developerSubdomain && subdomain !== developerEUSubdomain ? `https://developer.${domain}.com` : '';

  let targetSubdomain = 'www';

  if (subdomain.endsWith('eu')) {
    targetSubdomain = 'eu';
  }

  useEffect(() => {
    if (subdomain === developerSubdomain || subdomain === developerEUSubdomain) {
      if (window && window.location) {
        setTargetUrl(window.location.href);
      }
    }
  }, [url, subdomain]);

  useEffect(() => {
    waitForUsabilla(() => {
      window.usabilla_live('show');
    });
  }, []);

  return (
    <div data-testid="Home__Developer">
      <DeveloperWebBasePage id="Developer - Home" title="SurveyMonkey API Developer Portal">
        <If condition={user.isAuthenticated}>
          <Query query={developerApplications}>
            {({ loading, error, data }) => {
              return (
                <>
                  <If condition={!data || data?.developerApplications?.totalCount > 0 || error || loading}>
                    <div data-testid="Home__DeveloperLoggedInWithApp" className="container-home">
                      <Box mt={8} mb={9}>
                        <Box mb={4}>
                          <Typography variant="pageTitle" component="h1" align="center">
                            Power your app with the SurveyMonkey API
                          </Typography>
                        </Box>
                        <Box>
                          <Typography align="center">
                            <Button href="/api/v3/">VIEW DOCUMENTATION</Button>
                          </Typography>
                        </Box>
                        <div className="home-screenshots" alt="Screenshots of the SurveyMonkey Developer Portal" />
                      </Box>
                      <Grid>
                        <Grid.Item>
                          <div className="icon home-icon learn" />
                          <Box my={3}>
                            <Typography variant="pageTitle" component="h2" align="center">
                              Learn
                            </Typography>
                          </Box>
                          <Typography variant="body" component="p" align="center">
                            Start by reading our documentation and decide whether you&apos;re building a public or
                            private app.
                          </Typography>
                        </Grid.Item>
                        <Grid.Item>
                          <div className="icon home-icon develop" />
                          <Box my={3}>
                            <Typography variant="pageTitle" component="h2" align="center">
                              Develop
                            </Typography>
                          </Box>
                          <Typography variant="body" component="p" align="center">
                            As you&apos;re building, decide what scopes you&apos;ll need to request from users or ask
                            for higher call limits.
                          </Typography>
                        </Grid.Item>
                        <Grid.Item>
                          <div className="icon home-icon deploy" />
                          <Box my={3}>
                            <Typography variant="pageTitle" component="h2" align="center">
                              Deploy
                            </Typography>
                          </Box>
                          <Typography variant="body" component="p" align="center">
                            Once you&apos;re ready, deploy your app on the “Overview” page and track your user totals!
                          </Typography>
                        </Grid.Item>
                      </Grid>
                    </div>
                  </If>
                  <If condition={data?.developerApplications?.totalCount === 0}>
                    <div data-testid="Home__DeveloperLoggedInWithoutApp" className="container-home">
                      <Box mt={8} mb={9}>
                        <Box mb={4}>
                          <Typography variant="pageTitle" component="h1" align="center">
                            Create with the SurveyMonkey API!
                          </Typography>
                        </Box>
                        <Box mb={4}>
                          <Typography variant="sectionTitle" component="h3" align="center">
                            Enable high-powered data collection
                          </Typography>
                        </Box>
                        <Box>
                          <Typography align="center">
                            <Button href={`${prependStatic}/apps/?show-add-app`}>Create New App</Button>
                          </Typography>
                        </Box>
                      </Box>
                      <Grid>
                        <Grid.Item>
                          <Box ml={8} mr={3}>
                            <div className="icon home-icon unlock" />
                            <Box my={6} mx={3}>
                              <Typography variant="sectionTitle" component="h3" align="center">
                                Build a public app for the App Directory
                              </Typography>
                              <ul>
                                <li>
                                  <Typography variant="body">
                                    An unlimited number of users can access the app
                                  </Typography>
                                </li>
                                <li>
                                  <Typography variant="body">
                                    Developer only needs a basic account to start building
                                  </Typography>
                                </li>
                                <li>
                                  <Typography variant="body">Apps must be approved</Typography>
                                </li>
                              </ul>
                            </Box>
                            <Typography align="center">
                              <Button color="alt" href={`${prependUrl}/build-a-public-app`}>
                                Learn more
                              </Button>
                            </Typography>
                          </Box>
                        </Grid.Item>
                        <Grid.Item>
                          <Box mr={8} ml={3}>
                            <div className="icon home-icon lock" />
                            <Box my={6} mx={3}>
                              <Typography variant="sectionTitle" component="h3" align="center">
                                Build a private app for you and your Team
                              </Typography>
                              <ul>
                                <li>
                                  <Typography variant="body">Developer purchases all accounts</Typography>
                                </li>
                                <li>
                                  <Typography variant="body">Developer needs an ENTERPRISE seat</Typography>
                                </li>
                                <li>
                                  <Typography variant="body">API limits apply</Typography>
                                </li>
                              </ul>
                            </Box>
                            <Typography align="center">
                              <Button color="alt" href={`${prependUrl}/build-a-private-app`}>
                                Learn more
                              </Button>
                            </Typography>
                          </Box>
                        </Grid.Item>
                      </Grid>
                    </div>
                  </If>
                </>
              );
            }}
          </Query>
        </If>
        <If condition={!user.isAuthenticated}>
          <div data-testid="Home__DeveloperLoggedOut" className="container-home">
            <Box mt={8} mb={9}>
              <Box mb={2}>
                <Typography variant="pageTitle" component="h1" align="center">
                  Welcome to the SurveyMonkey API!
                </Typography>
              </Box>
              <Box mb={4}>
                <Typography variant="sectionTitle" component="h3" align="center">
                  Let&apos;s build together
                </Typography>
              </Box>
              <Box>
                <Typography align="center">
                  <Button
                    href={`https://${targetSubdomain}.${domain}.com/user/sign-in/?ep=${encodeURIComponent(
                      targetUrl
                    )}&amp;ut_source=dev_portal&amp;ut_source2=header`}
                  >
                    Get Started
                  </Button>
                </Typography>
              </Box>
            </Box>
            <Grid>
              <Grid.Item>
                <div className="icon home-icon customize-view" />
                <Box my={3}>
                  <Typography variant="pageTitle" component="h2" align="center">
                    Customize
                  </Typography>
                </Box>
                <Typography variant="body" component="p" align="center">
                  Power your app with customer behavior data.
                </Typography>
              </Grid.Item>
              <Grid.Item>
                <div className="icon home-icon automatic" />
                <Box my={3}>
                  <Typography variant="pageTitle" component="h2" align="center">
                    Automate
                  </Typography>
                </Box>
                <Typography variant="body" component="p" align="center">
                  As your data starts rolling in, take action on your findings immediately.
                </Typography>
              </Grid.Item>
              <Grid.Item>
                <div className="icon home-icon user-engagement-female" />
                <Box my={3}>
                  <Typography variant="pageTitle" component="h2" align="center">
                    Engage
                  </Typography>
                </Box>
                <Typography variant="body" component="p" align="center">
                  Respond instantly to customer feedback.
                </Typography>
              </Grid.Item>
            </Grid>
          </div>
        </If>
      </DeveloperWebBasePage>
    </div>
  );
};

export default Home;
